/**
 * Generation orchestrator. Runs once at startup; produces all GPU artifacts that
 * render passes consume. Each phase chains another subsystem here.
 *
 * Each subsystem's output texture is exposed under a stable name on `GenArtifacts`.
 * Render passes bind these views once at startup; the contents are populated before
 * the first render frame because GPU command ordering is FIFO on the same queue.
 */

import { config } from '../config.ts';
import { createHeightmap, type HeightmapModule } from './heightmap.ts';
import { runErosion } from './erosion.ts';
import { createBiome, type BiomeModule } from './biome.ts';
import { createRoads, type RoadsModule } from './roads.ts';
import { createBuildings, type BuildingsModule } from './buildings.ts';
import { createFlatten } from './flatten.ts';
import { createTrees, type TreesModule } from './trees.ts';

export interface GenArtifacts {
  heightmap: HeightmapModule;
  /** Building-footprint occupancy mask from the flatten pass (1.0 under lots).
   *  Exposed for the ?probe=1 diagnostic; owned/destroyed by the flatten module. */
  occupancy: GPUTexture;
  biome: BiomeModule;
  roads: RoadsModule;
  buildings: BuildingsModule;
  trees: TreesModule;
  destroy(): void;
}

export async function generate(device: GPUDevice, seed: number): Promise<GenArtifacts> {
  const initial = await createHeightmap(device, { seed });
  const eroded = await runErosion(device, initial, {
    seed,
    iterations: config.erosionIterations,
  });
  const biome = await createBiome(device, eroded, { seed });
  const roads = await createRoads(device, eroded, biome, { seed });
  const buildings = await createBuildings(device, eroded, biome, roads, { seed });
  // Stamp flattened platforms under each accepted building footprint. Downstream
  // stages (trees, terrain renderer, terrain-sampler) bind this in place of the
  // raw eroded heightmap so the level lots are visible and the camera respects them.
  const flattened = await createFlatten(device, eroded, buildings);
  const trees = await createTrees(device, flattened, biome, roads, flattened.occupancyView, { seed });

  return {
    heightmap: flattened,
    occupancy: flattened.occupancy,
    biome,
    roads,
    buildings,
    trees,
    destroy() {
      eroded.destroy();
      flattened.destroy();
      biome.destroy();
      roads.destroy();
      buildings.destroy();
      trees.destroy();
    },
  };
}
